/**
 * SelectTools - Utilities for working with HTML select elements.
 * Provides SelectObject for manipulating single selects and
 * SelectSwitcher for managing paired select lists (e.g., available/selected).
 */

// External dependencies

/** Flag indicating selecttools is loaded */
var _SELECT_TOOLS_: boolean = true;

/** Separator for values in comma-separated lists. Mutable global - external code (e.g. datapicker.jsp) may change this. */
declare var VALUE_SPACER: string;
(globalThis as any).VALUE_SPACER = (globalThis as any).VALUE_SPACER ?? ",";

/** Separator for names in pipe-separated lists. Mutable global - external code (e.g. datapicker.jsp) may change this. */
declare var NAME_SPACER: string;
(globalThis as any).NAME_SPACER = (globalThis as any).NAME_SPACER ?? "|";

/** Interface for NameVal objects used internally */
interface NameValInstance {
	value: string;
	text: string;
	sortName: string;
	selected: boolean;
	setName: (str: string) => void;
}

/** Interface for SelectObject instances */
interface SelectObjectInstance {
	element: HTMLSelectElement;
	options: NameValInstance[] | null;
	oLen: number;
	hiOption: number | null;
	loOption: number;
	get: (which?: string) => string;
	set: (values: string, which?: string) => void;
	numSelected: () => number;
	numOptions: () => number;
	hasValue: (value: string) => boolean;
	getAll: () => string;
	getNames: (values?: string) => string;
	setDefault: (values: string) => void;
	selectAll: () => void;
	insert: (names: string, values: string, allowDuplicates?: boolean) => void;
	remove: (values: string) => void;
	replace: (curValues: string | null, names: string, values: string) => void;
	sortedInsert: (names: string, values: string) => void;
	sort: () => void;
	reverse: () => void;
	swap: (a: number, b: number) => void;
	rename: (value: string, name: string) => void;
	moveSelected: (amount: number | string) => void;
	addPriv: (n: string, v: string, s: boolean) => void;
	insertPriv: (names: string, values: string, allowDuplicates: boolean, selected: boolean) => void;
	sortPriv: () => void;
	buildPriv: () => number;
	findPriv: (value: string) => number;
	removePriv: (index: number) => void;
	resetPriv: () => void;
	swapPriv: (a: number, b: number) => void;
	setRangePriv: (val: number) => void;
}

/** Interface for SelectSwitcher instances */
interface SelectSwitcherInstance {
	selects: SelectObjectInstance[];
	defaultText: string | undefined;
	autoSort: boolean | number;
	move: (from: number, values?: string) => void;
	insert: (to: number, names: string, values: string) => void;
	remove: (from: number, values: string) => void;
	selectAll: (which: number) => void;
}

//-------------- SelectObject --------------

/**
 * Constructor for SelectObject - wraps an HTML select element with utility methods.
 * @param formElement - The HTML select element to wrap
 */
function SelectObject(this: SelectObjectInstance, formElement: HTMLSelectElement): void {
	this.element = formElement;
	this.options = null;
	this.oLen = 0;
	this.hiOption = null;
	this.loOption = 0;
	this.get = getSO;
	this.set = setSO;
	this.numSelected = numSelectedSO;
	addAdvancedProperties(this);
}

/**
 * Add advanced manipulation methods to a SelectObject.
 * @param obj - The SelectObject to extend
 */
function addAdvancedProperties(obj: SelectObjectInstance): void {
	obj.numOptions = numOptionsSO;
	obj.hasValue = hasValueSO;
	obj.getAll = getAllSO;
	obj.getNames = getNamesSO;
	obj.setDefault = setDefaultSO;
	obj.selectAll = selectAllSO;
	obj.insert = insertSO;
	obj.remove = removeSO;
	obj.replace = replaceSO;
	obj.sortedInsert = sortedInsertSO;
	obj.sort = sortSO;
	obj.reverse = reverseSO;
	obj.swap = swapSO;
	obj.rename = renameSO;
	obj.moveSelected = moveSelectedSO;

	obj.addPriv = addPSO;
	obj.insertPriv = insertPSO;
	obj.sortPriv = sortPSO;
	obj.buildPriv = buildPSO;
	obj.findPriv = findPSO;
	obj.removePriv = removePSO;
	obj.resetPriv = resetPSO;
	obj.swapPriv = swapPSO;
	obj.setRangePriv = setRangePSO;
}

/**
 * Get the number of options in the select.
 */
function numOptionsSO(this: SelectObjectInstance): number {
	return this.element.length;
}

/**
 * Get the number of selected options.
 */
function numSelectedSO(this: SelectObjectInstance): number {
	return this.buildPriv();
}

/**
 * Get selected value(s) from the select.
 * @param which - Optional specific value to check
 * @returns Comma-separated string of selected values, or specific value if selected
 */
function getSO(this: SelectObjectInstance, which?: string): string {
	if (which != null) {
		this.buildPriv();
		var i = this.findPriv(which);
		return i == -1 ? "" : (this.options![i].selected ? which : "");
	}
	var returnVal: { [key: string]: boolean } = {};
	for (var i = 0; i < this.element.length; i++)
		if (this.element.options[i].selected)
			returnVal[this.element.options[i].value] = true;
	return setToString(returnVal, VALUE_SPACER);
}

/**
 * Check if a value exists in the select.
 * @param value - The value to check for
 */
function hasValueSO(this: SelectObjectInstance, value: string): boolean {
	this.buildPriv();
	return this.findPriv(value) != -1;
}

/**
 * Get all values from the select.
 * @returns Comma-separated string of all values
 */
function getAllSO(this: SelectObjectInstance): string {
	var returnVal: { [key: string]: boolean } = {};
	for (var i = 0; i < this.element.length; i++)
		returnVal[this.element.options[i].value] = true;
	return setToString(returnVal, VALUE_SPACER);
}

/**
 * Get the display names for given values.
 * @param values - Optional comma-separated values (defaults to selected values)
 * @returns Pipe-separated string of names
 */
function getNamesSO(this: SelectObjectInstance, values?: string): string {
	var i: number, value: string, returnVal: { [key: string]: boolean } = {};
	var valuesSet = stringToSet(values != null ? values : this.get(), VALUE_SPACER);
	this.buildPriv();
	for (value in valuesSet) {
		var idx = this.findPriv(value);
		if (idx != -1) returnVal[this.options![idx].text] = true;
	}
	return setToString(returnVal, NAME_SPACER);
}

/**
 * Set selected value(s) in the select.
 * @param values - Comma-separated values to select
 * @param which - Optional specific value to set
 */
function setSO(this: SelectObjectInstance, values: string, which?: string): void {
	if (which != null) {
		this.buildPriv();
		var i = this.findPriv(which);
		if (i != -1) this.element.options[i].selected = (values > "");
		return;
	}
	var valuesSet = stringToSet(values, VALUE_SPACER);
	for (var i = 0; i < this.element.length; i++)
		this.element.options[i].selected = valuesSet[this.element.options[i].value] == true;
}

/**
 * Set default selected values.
 * @param values - Comma-separated values to set as default
 */
function setDefaultSO(this: SelectObjectInstance, values: string): void {
	var valuesSet = stringToSet(values, VALUE_SPACER);
	for (var i = 0; i < this.element.length; i++)
		this.element.options[i].defaultSelected = valuesSet[this.element.options[i].value] == true;
}

/**
 * Select all options in the select.
 */
function selectAllSO(this: SelectObjectInstance): void {
	for (var i = 0; i < this.element.length; i++)
		this.element.options[i].selected = true;
}

/**
 * Insert new options into the select.
 * @param names - Pipe-separated display names
 * @param values - Comma-separated values
 * @param allowDuplicates - Whether to allow duplicate values
 */
function insertSO(this: SelectObjectInstance, names: string, values: string, allowDuplicates?: boolean): void {
	this.buildPriv();
	this.insertPriv(names, values, allowDuplicates || false, false);
	this.resetPriv();
}

/**
 * Remove options by value.
 * @param values - Comma-separated values to remove
 */
function removeSO(this: SelectObjectInstance, values: string): void {
	var valuesSet = stringToSet(values, VALUE_SPACER);
	for (var i = 0; i < this.element.length; i++)
		if (valuesSet[this.element.options[i].value]) {
			this.element.options[i] = null as any;
			i--;
			this.options = null;
		}
}

/**
 * Replace existing options with new ones.
 * @param curValues - Values to replace (null for all)
 * @param names - New display names
 * @param values - New values
 */
function replaceSO(this: SelectObjectInstance, curValues: string | null, names: string, values: string): void {
	if (curValues == null) curValues = this.getAll();
	this.buildPriv();
	var curValuesSet = stringToSet(curValues, VALUE_SPACER);
	var valuesMap = stringsToMap(values, names, VALUE_SPACER, NAME_SPACER);
	var curValue: string, i = 0, j = 0, vals: string[] = [], indxs: number[] = [];
	for (curValue in curValuesSet) indxs[i++] = this.findPriv(curValue);
	for (curValue in valuesMap) vals[j++] = curValue;
	j = 0;
	for (i = 0; i < indxs.length && j < vals.length; i++)
		if (indxs[i] != -1) {
			this.options![indxs[i]].value = vals[j];
			this.options![indxs[i]].setName(valuesMap[vals[j++]]);
			this.setRangePriv(indxs[i]);
		}
	while (i < indxs.length) {
		for (var k = i + 1; k < indxs.length; k++)
			if (indxs[k] > indxs[i]) indxs[k]--;
		this.removePriv(indxs[i++]);
	}
	while (j < vals.length) this.addPriv(valuesMap[vals[j]], vals[j++], false);
	this.resetPriv();
}

/**
 * Reverse the order of options.
 */
function reverseSO(this: SelectObjectInstance): void {
	this.buildPriv();
	var i: number, stop = this.oLen / 2, max = this.oLen - 1;
	for (i = 0; i < stop; i++)
		this.swapPriv(i, max - i);
	this.resetPriv();
}

/**
 * Sort options alphabetically by display name.
 */
function sortSO(this: SelectObjectInstance): void {
	this.buildPriv();
	this.sortPriv();
	this.resetPriv();
}

/**
 * Insert options and sort the result.
 * @param names - Pipe-separated display names
 * @param values - Comma-separated values
 */
function sortedInsertSO(this: SelectObjectInstance, names: string, values: string): void {
	this.buildPriv();
	if (this.getAll() == "") this.oLen = 0;
	for (var i = 0; i < this.oLen; i++) {
		if (this.options![i].selected) this.setRangePriv(i);
		this.options![i].selected = false;
	}
	this.insertPriv(names, values, false, true);
	this.sortPriv();
	this.resetPriv();
}

/**
 * Swap two options by index.
 * @param a - First index
 * @param b - Second index
 */
function swapSO(this: SelectObjectInstance, a: number, b: number): void {
	this.swapPriv(a, b);
	this.resetPriv();
}

/**
 * Rename an option by value.
 * @param value - The value to find
 * @param name - The new display name
 */
function renameSO(this: SelectObjectInstance, value: string, name: string): void {
	this.buildPriv();
	var i = this.findPriv(value);
	if (i != -1) {
		this.options![i].setName(name);
		this.setRangePriv(i);
		this.resetPriv();
	}
}

/**
 * Move selected items in a select element.
 * @param name - Element name
 * @param amount - Number of positions to move, or "top"/"bottom"
 */
function moveSelected(name: string, amount: number | string): void {
	var e = getElementObj(name);
	if (e == null) return;
	if (amount == "top") amount = -e.numOptions();
	if (amount == "bottom") amount = e.numOptions();
	e.moveSelected(amount as number);
}

/**
 * Move selected options up or down.
 * @param amount - Number of positions to move (negative = up, positive = down)
 */
function moveSelectedSO(this: SelectObjectInstance, amount: number | string): void {
	if (amount == 0 || this.buildPriv() == 0) return;

	var inc: number, start: number, stop: number, i: number, j: number, o = this.options!;
	var numAmount = typeof amount === 'number' ? amount : parseInt(amount as string);
	if (numAmount < 0) {
		inc = 1; start = 1; stop = this.oLen; numAmount = -numAmount;
	} else {
		inc = -1; start = this.oLen - 2; stop = -1;
	}
	for (j = 0; j < numAmount; j++)
		for (i = start; i != stop; i += inc)
			if (o[i].selected && !o[i - inc].selected)
				this.swapPriv(i, i - inc);

	this.resetPriv();
}

/**
 * Private: Insert options into internal array.
 */
function insertPSO(this: SelectObjectInstance, names: string, values: string, allowDuplicates: boolean, selected: boolean): void {
	var value: string, allValues = allowDuplicates ? {} : stringToSet(this.getAll(), VALUE_SPACER);
	var valuesMap = stringsToMap(values, names, VALUE_SPACER, NAME_SPACER);
	for (value in valuesMap) {
		if (allValues[value] != true)
			this.addPriv(valuesMap[value], value, selected);
		allValues[value] = true;
	}
}

/**
 * Private: Sort using quicksort.
 */
function sortPSO(this: SelectObjectInstance): void {
	quickSort(this, 0, this.oLen);
}

/**
 * Bubble sort for small arrays.
 */
function bubbleSort(e: SelectObjectInstance, lo: number, hi: number): void {
	var o = e.options!, i: number, j: number;
	for (i = lo + 1; i < hi; i++)
		for (j = i; j > lo; j--)
			if (o[j].sortName < o[j - 1].sortName)
				e.swapPriv(j, j - 1);
}

/**
 * Partition for quicksort.
 */
function partition(e: SelectObjectInstance, lo: number, hi: number): number {
	var o = e.options!;
	hi--;
	e.swapPriv(lo, (lo + hi) >> 1);
	while (true) {
		while (lo < hi && o[lo].sortName < o[hi].sortName) hi--;
		if (lo >= hi) return lo;
		e.swapPriv(lo++, hi);

		while (lo < hi && o[lo].sortName < o[hi].sortName) lo++;
		if (lo >= hi) return hi;
		e.swapPriv(lo, hi--);
	}
}

/**
 * Quicksort implementation.
 */
function quickSort(e: SelectObjectInstance, lo: number, hi: number): void {
	if (hi - lo <= 10) {
		bubbleSort(e, lo, hi);
		return;
	}
	var m = partition(e, lo, hi);
	quickSort(e, lo, m);
	quickSort(e, m + 1, hi);
}

/**
 * Constructor for NameVal - internal representation of an option.
 */
function NameVal(this: NameValInstance, name: string, value: string, selected: boolean): void {
	this.value = value;
	this.selected = selected;
	this.text = "";
	this.sortName = "";
	this.setName = setNameNV;
	this.setName(name);
}

/**
 * Set the name and compute sort key.
 */
function setNameNV(this: NameValInstance, str: string): void {
	var c: string, out = "", onLtr = false;
	this.text = str;
	if (this.value == "") { this.sortName = " "; return; }
	str = str.toLowerCase();
	for (var i = 0; i < str.length; i++) {
		c = str.charAt(i);
		if (c >= "a" && c <= "z" || c >= "0" && c <= "9") {
			out += c;
			onLtr = true;
		} else if (onLtr) {
			out += ' ';
			onLtr = false;
		}
	}
	this.sortName = out == 'other' ? "~" : out;
}

/**
 * Private: Build internal options array from DOM.
 */
function buildPSO(this: SelectObjectInstance): number {
	var e = this.element, num = 0, i: number, create = false;
	if (this.options == null || this.hiOption != null || this.oLen != e.length) {
		this.options = [];
		this.hiOption = null;
		create = true;
		this.oLen = e.length;
	}
	var o = this.options;
	for (i = 0; i < this.oLen; i++) {
		if (create)
			o[i] = new (NameVal as any)(e.options[i].text, e.options[i].value, e.options[i].selected);
		else
			o[i].selected = e.options[i].selected;
		if (o[i].selected) num++;
	}
	return num;
}

/**
 * Private: Reset DOM from internal options array.
 */
function resetPSO(this: SelectObjectInstance): void {
	var e = this.element;
	var curLen = e.length, o = this.options!;
	var i: number;
	for (i = this.loOption; i < curLen && i <= this.hiOption!; i++) {
		e.options[i].text = o[i].text;
		e.options[i].value = o[i].value;
		e.options[i].selected = o[i].selected;
	}
	for (; i < this.oLen && i <= this.hiOption!; i++) {
		e.options[i] = new Option(o[i].text, o[i].value, false, o[i].selected);
	}
	while (e.options.length > this.oLen)
		e.options[e.options.length - 1] = null as any;
	this.hiOption = null;
}

/**
 * Private: Swap two options in internal array.
 */
function swapPSO(this: SelectObjectInstance, a: number, b: number): void {
	if (this.options == null) this.buildPriv();
	var o = this.options!;
	if (a >= this.oLen || a < 0 || b >= this.oLen || b < 0) return;
	var temp = o[a]; o[a] = o[b]; o[b] = temp;
	this.setRangePriv(a); this.setRangePriv(b);
}

/**
 * Private: Add an option to internal array.
 */
function addPSO(this: SelectObjectInstance, n: string, v: string, s: boolean): void {
	this.options![this.oLen++] = new (NameVal as any)(n, v, s);
	this.setRangePriv(this.oLen - 1);
}

/**
 * Private: Find option index by value.
 */
function findPSO(this: SelectObjectInstance, value: string): number {
	for (var i = 0; i < this.oLen; i++)
		if (this.options![i].value == value)
			return i;
	return -1;
}

/**
 * Private: Remove option at index.
 */
function removePSO(this: SelectObjectInstance, index: number): void {
	this.oLen--;
	for (var i = index; i < this.oLen; i++) {
		this.setRangePriv(i);
		this.options![i] = this.options![i + 1];
	}
}

/**
 * Private: Track range of modified options.
 */
function setRangePSO(this: SelectObjectInstance, val: number): void {
	if (this.hiOption == null) {
		this.hiOption = val;
		this.loOption = val;
	} else {
		if (this.hiOption < val) this.hiOption = val;
		if (this.loOption > val) this.loOption = val;
	}
}

//-------------- SelectSwitcher --------------

/**
 * Constructor for SelectSwitcher - manages paired select lists.
 * @param formName - Name of the form
 * @param selectName0 - Name of the first select (available)
 * @param selectName1 - Name of the second select (selected)
 * @param defaultText - Optional placeholder text when list is empty
 */
function SelectSwitcher(this: SelectSwitcherInstance, formName: string, selectName0: string, selectName1: string, defaultText?: string): void {
	this.selects = new Array(2);
	this.selects[0] = getSelectObject(formName, selectName0);
	this.selects[1] = getSelectObject(formName, selectName1);

	this.defaultText = defaultText;
	this.move = moveSS;
	this.insert = insertSS;
	this.remove = removeSS;
	this.selectAll = selectAllSS;
	this.autoSort = true;
}

/**
 * Move options from one select to another.
 * @param from - Source select index (0 or 1)
 * @param values - Optional specific values to move
 */
function moveSS(this: SelectSwitcherInstance, from: number, values?: string): void {
	if (values == null) values = this.selects[from].get();
	if (values == "") return;
	var to = 1 - from;
	this.insert(to, this.selects[from].getNames(values), values);
	this.selects[to].set(values);
	this.remove(from, values);
}

/**
 * Insert options into a select.
 * @param to - Target select index (0 or 1)
 * @param names - Pipe-separated display names
 * @param values - Comma-separated values
 */
function insertSS(this: SelectSwitcherInstance, to: number, names: string, values: string): void {
	var nameCount = countTokens(names, NAME_SPACER);
	var valueCount = countTokens(values, VALUE_SPACER);
	if (nameCount != valueCount || valueCount == 0) return;

	if (this.autoSort == true || this.autoSort == to)
		this.selects[to].sortedInsert(names, values);
	else
		this.selects[to].insert(names, values);
	if (this.defaultText) this.selects[to].remove("");
	if (debug) window.status = this.selects[0].getAll() + "~|~" + this.selects[1].getAll();
}

/**
 * Remove options from a select.
 * @param from - Source select index (0 or 1)
 * @param values - Comma-separated values to remove
 */
function removeSS(this: SelectSwitcherInstance, from: number, values: string): void {
	var valueCount = countTokens(values, VALUE_SPACER);
	if (valueCount == 0) return;
	if (this.defaultText && this.selects[from].numOptions() == valueCount)
		this.selects[from].insert(this.defaultText, "");
	this.selects[from].remove(values);
}

/**
 * Select all options in a select.
 * @param which - Select index (0 or 1)
 */
function selectAllSS(this: SelectSwitcherInstance, which: number): void {
	this.selects[which].selectAll();
}

//-------------- Utilities --------------

/**
 * Count tokens in a separated string.
 * @param str - The string to count
 * @param separator - The separator character
 */
function countTokens(str: string, separator: string): number {
	return objSize(stringToSet(str, separator));
}

/**
 * Get a SelectObject for a form element.
 * @param formName - Name of the form
 * @param selectName - Name of the select element
 */
function getSelectObject(formName: string, selectName: string): SelectObjectInstance {
	if (_ERROR_REPORTER_ == true && getFormObj(formName) != null)
		return getFormObj(formName)!.getElement(selectName);
	return new (SelectObject as any)(document.forms[formName as any].elements[selectName as any]);
}

/**
 * Convert a set object to a separated string.
 * @param set - Object with boolean values
 * @param separator - The separator character
 */
function setToString(set: { [key: string]: boolean }, separator: string): string {
	var i: string, out: string | null = null;
	for (i in set) {
		if (set[i]) {
			if (out == null) out = ""; else out += separator;
			out += i;
		}
	}
	return out || "";
}

/**
 * Convert a separated string to a set object.
 * @param str - The string to parse
 * @param separator - The separator character
 */
function stringToSet(str: string | null, separator: string): { [key: string]: boolean } {
	var out: { [key: string]: boolean } = {}, prev: number, pos = -1;
	if (str == null) return out;
	do {
		prev = pos + 1;
		pos = str.indexOf(separator, prev);
		out[str.substring(prev, pos == -1 ? str.length : pos)] = true;
	} while (pos != -1);
	return out;
}

/**
 * Convert two separated strings to a key-value map.
 * @param keys - Comma-separated keys
 * @param values - Pipe-separated values
 * @param separatorK - Key separator
 * @param separatorV - Value separator
 */
function stringsToMap(keys: string | null, values: string | null, separatorK: string, separatorV: string): { [key: string]: string } {
	var out: { [key: string]: string } = {}, prevK: number, prevV: number, posK = -1, posV = -1;
	if (keys == null || values == null) return out;
	do {
		prevK = posK + 1; prevV = posV + 1;
		posK = keys.indexOf(separatorK, prevK);
		posV = values.indexOf(separatorV, prevV);
		out[keys.substring(prevK, posK == -1 ? keys.length : posK)] =
			values.substring(prevV, posV == -1 ? values.length : posV);
	} while (posK != -1 && posV != -1);
	return out;
}

/**
 * Count properties in an object.
 * @param obj - The object to count
 */
function objSize(obj: object): number {
	var i: string, s = 0;
	for (i in obj) s++;
	return s;
}

/**
 * Count true values in a set.
 * @param set - Object with boolean values
 */
function setSize(set: { [key: string]: boolean }): number {
	var i: string, s = 0;
	for (i in set)
		if (set[i]) s++;
	return s;
}

// Export to globalThis for backwards compatibility
(globalThis as any)._SELECT_TOOLS_ = _SELECT_TOOLS_;
(globalThis as any).SelectObject = SelectObject;
(globalThis as any).SelectSwitcher = SelectSwitcher;
(globalThis as any).addAdvancedProperties = addAdvancedProperties;
(globalThis as any).moveSelected = moveSelected;
(globalThis as any).countTokens = countTokens;
(globalThis as any).getSelectObject = getSelectObject;
(globalThis as any).setToString = setToString;
(globalThis as any).stringToSet = stringToSet;
(globalThis as any).stringsToMap = stringsToMap;
(globalThis as any).objSize = objSize;
(globalThis as any).setSize = setSize;

if (_SR_ != null) _SR_.notify("selecttools.js");

// ES module exports
export {
	_SELECT_TOOLS_,
	SelectObject,
	SelectSwitcher,
	addAdvancedProperties,
	moveSelected,
	countTokens,
	getSelectObject,
	setToString,
	stringToSet,
	stringsToMap,
	objSize,
	setSize
};
export type { SelectObjectInstance, SelectSwitcherInstance, NameValInstance };
